import { action, computed, makeObservable, observable } from 'mobx';
import { PresetViewScope, type PresetView, type PresetViewConfig, type PresetViewSupportedView } from '@bringg/types';
import i18next from 'i18next';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import { sendPresetViewsChangedEvent } from 'bringg-web/features/preset-views/utils';
import notification from 'bringg-web/services/notification';

export abstract class PresetViewBase {
	id: number = null;

	protected serverPresetView: PresetView = null;
	edited = false;
	loading = false;

	constructor(preset: PresetView) {
		this.id = preset.id;
		this.serverPresetView = preset;

		makeObservable(this, {
			id: observable,
			edited: observable.ref,
			loading: observable.ref,
			name: computed,
			scope: computed,
			view: computed,
			isDefault: computed,
			reset: action,
			setEdited: action,
			setIsLoading: action
		});
	}

	abstract assignServerToClient();
	abstract parseClientToServer(): PresetView;

	save = async () => {
		try {
			await getRootEnv().dashboardSdk.sdk.v2.presetViews().patch(this.parseClientToServer());

			notification.success(i18next.t('PRESET_VIEWS.PRESET_SAVED', { name: this.name }));
			this.setEdited(false);
		} catch (error) {
			notification.error(i18next.t('PRESET_VIEWS.FAILED_TO_SAVE', { name: this.name }));
		}
	};

	activatePresetForView = async (sendApplicationEvent = true) => {
		try {
			this.setIsLoading(true);

			await getRootEnv().dashboardSdk.sdk.v2.userPresetViews.update({
				view: this.view,
				preset_view_id: this.id
			});

			if (sendApplicationEvent) {
				sendPresetViewsChangedEvent({
					view: this.view,
					preset_view_id: this.id
				});
			}
		} catch (error) {
			const toastText = i18next.t('PRESET_VIEWS.FAIL_CHANGE_ACTIVE_USER_PRESET');
			console.error(toastText, error);
			notification.error(toastText);
		} finally {
			this.setIsLoading(false);
		}
	};

	reset = (sendApplicationEvent = true) => {
		this.assignServerToClient();
		this.setEdited(false);

		if (sendApplicationEvent) {
			sendPresetViewsChangedEvent({
				view: this.view,
				preset_view_id: this.id
			});
		}
	};

	setEdited = (value: boolean) => {
		this.edited = value;
	};

	setIsLoading = (loading: boolean) => {
		this.loading = loading;
	};

	saveAsNewPreset = async (name: string) => {
		this.setIsLoading(true);
		const parsed = this.parseClientToServer();

		const response = await getRootEnv().dashboardSdk.sdk.v2.presetViews().create({
			name,
			view: this.view,
			scope: PresetViewScope.User,
			view_config: parsed.view_config
		});

		this.reset(false);
		this.setEdited(false);
		this.setIsLoading(false);

		return response;
	};

	deletePreset = async (sendApplicationEvent = true) => {
		this.setIsLoading(true);

		try {
			await getRootEnv().dashboardSdk.sdk.v2.presetViews().delete(this.id);

			if (sendApplicationEvent) {
				sendPresetViewsChangedEvent();
			}
			notification.success(i18next.t('PRESET_VIEWS.DELETE_SUCCESS', { name: this.name }));
			this.setIsLoading(false);
		} catch (e) {
			// yet error is too generic even to distinguish between 5xx and 4xx errors
			// must be improved by https://bringg.atlassian.net/browse/BRNGG-30783
			notification.error(
				`${i18next.t('PRESET_VIEWS.DELETE_FAIL', { name: this.name })}. ${i18next.t(
					'PRESET_VIEWS.USED_BY_OTHER_USERS'
				)}`
			);
			this.setIsLoading(false);
			throw e;
		}
	};

	promoteToMerchantLevel = async () => {
		this.setIsLoading(true);

		try {
			await getRootEnv()
				.dashboardSdk.sdk.v2.presetViews()
				.patch({
					// first preset of merchant/user is migrated and does has invalid date\
					// to save preset it should be parsed to valid on(api required filed add)
					// be aware that if preset was edited and then promoted in ManagePreset Modal the current preset state(with all edited changes) will be saved
					// meanwhile we are not allowed user change scope when preset is edited
					...this.parseClientToServer(),
					scope: PresetViewScope.Merchant
				});
			notification.success(i18next.t('PRESET_VIEWS.PROMOTE_TO_MERCHANT_SCOPE_SUCCESS', { name: this.name }));
		} catch (e) {
			notification.success(i18next.t('PRESET_VIEWS.UPDATE_FAIL', { name: this.name }));
		}

		this.setIsLoading(false);
	};

	changePresetName = async (newName: string) => {
		this.setIsLoading(true);

		try {
			await getRootEnv()
				.dashboardSdk.sdk.v2.presetViews()
				.patch({
					// first preset of merchant/user is migrated and does has invalid date\
					// to save preset it should be parsed to valid on(api required filed add)
					// be aware that if preset was edited and then name changed in ManagePreset Modal the current preset state(with all edited changes) will be saved
					// meanwhile we are not allowed user change name when preset is edited
					...this.parseClientToServer(),
					name: newName
				});
			notification.success(i18next.t('PRESET_VIEWS.RENAME_SUCCESS', { name: newName }));
		} catch (e) {
			notification.success(i18next.t('PRESET_VIEWS.UPDATE_FAIL', { name: this.name }));
		}

		this.setIsLoading(false);
	};

	get name(): string {
		return this.serverPresetView?.name;
	}

	get scope(): PresetViewScope {
		return this.serverPresetView?.scope;
	}

	get view(): PresetViewSupportedView {
		return this.serverPresetView?.view;
	}

	get isDefault(): boolean {
		return this.serverPresetView?.default;
	}

	get prevPresetViewColumns(): PresetViewConfig['columns'] {
		return this.serverPresetView?.view_config.columns;
	}
}
